import { createSlice } from '@reduxjs/toolkit';
import {
  currentSupplierIntegrationExtraReducer,
  getUserDetailsExtraReducer,
  logoutUserExtraReducer,
  resetPasswordExtraReducer,
  firebaseSendLoginEmailExtraReducer,
  firebaseConfirmLoginUserExtraReducer,
  checkUserExistsExtraReducer,
  getPlaidLinkTokenExtraReducer,
  updateLoggedInUserPasswordExtraReducer,
  firebaseLoginEmailAndPasswordExtraReducer,
  firebaseConfirmPasswordResetExtraReducer,
  getUserBuyersExtraReducer,
  referBuyerUsersExtraReducer,
  referSubSupplierUsersExtraReducer,
  updateSubUserExtraReducer,
  checkUserIsSetupWithCompanyExtraReducer,
  getStripePublishableKeyExtraReducer,
  getRutterPublicKeyExtraReducer,
  processRutterPublicTokenExtraReducer,
  firebaseSendPasswordResetEmailExtraReducer,
  updateUserDetailsExtraReducer,
  updateUserProfileExtraReducer,
  createUserSignatureExtraReducer,
  getUserSignaturesExtraReducer,
  deleteUserSignatureExtraReducer,
  newUserNotifyDillExtraReducer,
  getCompanyUsersExtraReducer,
  getMentionableUsersExtraReducer,
  getUsersExtraReducer,
  getPlaidLinkTokenNoAuthExtraReducer,
  getCurrentSupplierExtraReducer,
  getUserLinkedSuppliersExtraReducer,
} from '../reducers/authReducers';
import type { PayloadAction } from '@reduxjs/toolkit';
import { AppUserApiResponse, Supplier, UserSignature } from '@dill/dill-shared';

export interface AuthInitialState {
  loadingList: string[];
  isLoginEmailSent: boolean;
  isValidatingUser: boolean;
  userCheckState: string;
  userCheckRoles: string[];
  error: string | null;
  authToken: string | null;
  companyId: string | null;
  user: AppUserApiResponse | null;
  currentSupplier: Supplier | null;
  userSuppliers: Supplier[];
  isCurrentSupplierIntegrated: boolean;
  firebaseUserId: string | null;
  globalPastPath: string;
  stripePublishableKey: string;
  rutterPublicKey: string;
  plaidTokenDetails: any;
  isPasswordSet: boolean;
  isEmailVerified: boolean;
  userBuyers: AppUserApiResponse[];
  userSignatures: UserSignature[];
  users: AppUserApiResponse[];
  mentionableUsers: AppUserApiResponse[];
  ssoLoading: boolean;
  ssoError: string | null;
}

const initialState: AuthInitialState = {
  loadingList: [],
  isLoginEmailSent: false,
  isValidatingUser: false,
  userCheckState: '',
  userCheckRoles: [],
  globalPastPath: '',
  stripePublishableKey: '',
  rutterPublicKey: '',
  error: null,
  authToken: null,
  companyId: null,
  user: null,
  currentSupplier: null,
  userSuppliers: [],
  isCurrentSupplierIntegrated: false,
  firebaseUserId: null,
  plaidTokenDetails: null,
  isPasswordSet: false,
  isEmailVerified: false,
  userBuyers: [],
  userSignatures: [],
  users: [],
  mentionableUsers: [],
  ssoLoading: false,
  ssoError: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthRootState: (state) => ({
      ...initialState,
      stripePublishableKey: state.stripePublishableKey ? state.stripePublishableKey : '',
      firebaseUserId: '',
    }),
    updateLoginState: (state, action: PayloadAction<string>) => {
      state.firebaseUserId = action.payload;
      if (!action.payload) {
        state.userCheckState = '';
        state.userCheckRoles = [];
      }
    },
    updateGlobalPastPath: (state, action: PayloadAction<string>) => {
      state.globalPastPath = action.payload;
    },
    resetUserCheck: (state) => {
      state.userCheckState = '';
      state.userCheckRoles = [];
    },
    updateSendLoginEmailState: (state, action: PayloadAction<boolean>) => {
      state.isLoginEmailSent = action.payload;
    },
    updateIsPasswordSet: (state, action: PayloadAction<boolean>) => {
      state.isPasswordSet = action.payload;
    },
    updateIsEmailVerified: (state, action: PayloadAction<boolean>) => {
      state.isEmailVerified = action.payload;
    },
    startProcessProgress: (state, action: PayloadAction<string>) => {
      state.loadingList = [...new Set([...state.loadingList, action.payload])];
    },
    endProcessProgress: (state, action: PayloadAction<string>) => {
      state.loadingList = state.loadingList.filter((item) => item !== action.payload);
    },
    // No-op: `branches` moved off Supplier onto the supplierAdditionalDetails subcollection (see
    // supplierAdditionalDetailsSchema.ts), synced instead via updateSupplierBranches's own
    // extraReducer into state.supplierBranches. Kept as an action (rather than removing it and
    // its call sites) so existing dispatches of it stay harmless rather than becoming dead code
    // to strip everywhere at once.
    updateUserSupplierBranches: (_state, _action: PayloadAction<any[]>) => {},
    updateUserSupplierDetails: (state, action: PayloadAction<Partial<Supplier>>) => {
      if (state.currentSupplier) {
        state.currentSupplier = { ...state.currentSupplier, ...action.payload } as any;
      }
    },
  },
  extraReducers(builder) {
    currentSupplierIntegrationExtraReducer(builder);
    getUserDetailsExtraReducer(builder);
    logoutUserExtraReducer(builder);
    resetPasswordExtraReducer(builder);
    firebaseSendLoginEmailExtraReducer(builder);
    firebaseSendPasswordResetEmailExtraReducer(builder);
    firebaseConfirmLoginUserExtraReducer(builder);
    checkUserExistsExtraReducer(builder);
    getPlaidLinkTokenExtraReducer(builder);
    getPlaidLinkTokenNoAuthExtraReducer(builder);
    firebaseLoginEmailAndPasswordExtraReducer(builder);
    updateLoggedInUserPasswordExtraReducer(builder);
    firebaseConfirmPasswordResetExtraReducer(builder);
    getUserBuyersExtraReducer(builder);
    referBuyerUsersExtraReducer(builder);
    referSubSupplierUsersExtraReducer(builder);
    updateSubUserExtraReducer(builder);
    checkUserIsSetupWithCompanyExtraReducer(builder);
    getStripePublishableKeyExtraReducer(builder);
    getRutterPublicKeyExtraReducer(builder);
    processRutterPublicTokenExtraReducer(builder);
    updateUserDetailsExtraReducer(builder);
    updateUserProfileExtraReducer(builder);
    createUserSignatureExtraReducer(builder);
    getUserSignaturesExtraReducer(builder);
    deleteUserSignatureExtraReducer(builder);
    newUserNotifyDillExtraReducer(builder);
    getCompanyUsersExtraReducer(builder);
    getMentionableUsersExtraReducer(builder);
    getUsersExtraReducer(builder);
    getCurrentSupplierExtraReducer(builder);
    getUserLinkedSuppliersExtraReducer(builder);
  },
});

export const {
  resetAuthRootState,
  updateLoginState,
  updateIsPasswordSet,
  updateIsEmailVerified,
  updateSendLoginEmailState,
  resetUserCheck,
  startProcessProgress,
  endProcessProgress,
  updateGlobalPastPath,
  updateUserSupplierBranches,
  updateUserSupplierDetails,
} = authSlice.actions;
